import * as React from "react";
import { useFormik } from "formik";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { Avatar } from "@nextui-org/avatar";
import { Button } from "@nextui-org/button";
import { Divider } from "@nextui-org/divider";
import ReCAPTCHA from "react-google-recaptcha";
import { Input, Textarea } from "@nextui-org/input";
import { Select, SelectItem } from "@nextui-org/select";
import { Card, CardBody, CardHeader } from "@nextui-org/card";

import { ApiResponseInterface } from "@/interfaces";
import { contactSchema } from "../helpers/validators";
import { makeRequest } from "../helpers/request";

import sectionImage from "../../public/assets/images/legal.png";
import us from "../../public/assets/images/countries/us.png";
import ng from "../../public/assets/images/countries/ng.png";
import gh from "../../public/assets/images/countries/gh.png";
import au from "../../public/assets/images/countries/au.png";
import sa from "../../public/assets/images/countries/sa.png";
import gb from "../../public/assets/images/countries/gb.png";
import ca from "../../public/assets/images/countries/ca.png";
import eu from "../../public/assets/images/countries/eu.png";

const countries = [
  { id: 1, name: "United States", img: us },
  { id: 2, name: "Australia", img: au },
  { id: 3, name: "Ghana", img: gh },
  { id: 4, name: "Nigeria", img: ng },
  { id: 5, name: "South Africa", img: sa },
  { id: 6, name: "United Kingdom", img: gb },
  { id: 7, name: "Canada", img: ca },
  { id: 8, name: "Others", img: eu },
];

export default function ContactUsPage() {
  const recaptchaRef = React.createRef<ReCAPTCHA>();

  const [loading, setLoading] = React.useState(false);
  const [formSuccess, setFormSuccess] = React.useState(false);
  const [formSuccessMessage, setFormSuccessMessage] = React.useState("");

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      country: "",
      message: "",
    },
    validationSchema: contactSchema,
    onSubmit: async (values) => handleFormSubmit(values),
  });

  const { errors, touched, values, handleChange, handleSubmit } = formik;

  const checkBoolean = (
    expressionOne: string | undefined,
    expressionTwo?: boolean | undefined,
  ): boolean => {
    if (expressionOne !== undefined && expressionTwo !== undefined) {
      return expressionOne.length > 0 && expressionTwo;
    }
    return false;
  };

  const handleFormSubmit = async (values: any) => {
    setLoading(true);
    values.form_token = import.meta.env.VITE_APP_AUTH_KEY ?? "";
    const token = recaptchaRef?.current?.getValue();
    values.token = token ?? "";

    const formResponse: ApiResponseInterface = await makeRequest(
      "/api/submit-contact",
      "POST",
      values,
    );

    if (formResponse.status == false) {
      toast.dismiss();
      setLoading(false);
      toast.error(formResponse.data, { position: "bottom-right" });
    } else {
      toast.dismiss();
      toast.success(formResponse.data.message, { position: "bottom-right" });
      setFormSuccess(true);
      setFormSuccessMessage(formResponse.data.message);
      setLoading(false);
    }
  };

  return (
    <div className="flex ct-hero ct-white-section w-full justify-center md:mx-auto">
      <div className="md:container xl:columns-2 px-8 lg:columns-2 gap-4 mt-[80px]">
        <div className="lg:w-full xl:w-full md:w-full sm:w-full ct-description p-3 sm:p-2 break-after-column mb-8 w-full">
          <Card>
            <CardHeader className="flex gap-3">
              <div className="icon-box ct-faint-bg flex flex-col justify-center items-center mx-2 my-2 rounded-md">
                <Icon
                  icon="mdi:envelope-fast-outline"
                  fontSize={36}
                  className="text-primary"
                />
              </div>
              <div className="flex flex-col">
                <p className="text-md">Contact Us</p>
                <p className="text-small text-default-500">
                  Send us message and our representation will be in touch within
                  12hrs
                </p>
              </div>
            </CardHeader>
            <Divider />
            <CardBody>
              {formSuccess ? (
                <div className="ct-success-container flex flex-col justify-center items-center">
                  <h3 className="text-lg lg:text-xl text-default-600 font-medium block max-w-full">
                    {formSuccessMessage}
                  </h3>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mt-3 mb-5 flex flex-row gap-6 justify-center items-center">
                    <Input
                      isRequired
                      className="max-w-xs"
                      type="text"
                      label="Full Name"
                      placeholder="Enter your full name"
                      size="lg"
                      onChange={handleChange}
                      name="name"
                      isInvalid={checkBoolean(errors.name, touched.name)}
                      value={values.name}
                      errorMessage={errors.name}
                      fullWidth={true}
                      labelPlacement="outside"
                    />
                    <Input
                      isRequired
                      className="max-w-xs"
                      errorMessage={errors.email}
                      fullWidth={true}
                      isInvalid={checkBoolean(errors.email, touched.email)}
                      label="Email Address"
                      labelPlacement="outside"
                      name="email"
                      placeholder="Enter your email address"
                      size="lg"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="my-5 flex flex-row gap-6 justify-center items-center">
                    <Input
                      isRequired
                      className="max-w-xs"
                      type="text"
                      label="Phone"
                      placeholder="Enter your phone number"
                      size="lg"
                      onChange={handleChange}
                      name="phone"
                      isInvalid={checkBoolean(errors.phone, touched.phone)}
                      value={values.phone}
                      errorMessage={errors.phone}
                      fullWidth={true}
                      labelPlacement="outside"
                    />
                    <Select
                      isRequired
                      className="max-w-xs"
                      errorMessage={errors.country}
                      fullWidth={true}
                      isInvalid={checkBoolean(errors.country, touched.country)}
                      label="Choose your country"
                      labelPlacement="outside"
                      name="country"
                      placeholder="Select your country"
                      size="lg"
                      value={values.country}
                      onChange={handleChange}
                    >
                      {countries.map((country) => (
                        <SelectItem
                          key={country.name}
                          textValue={country.name}
                          startContent={
                            <Avatar
                              alt={country.name}
                              className="flex-shrink-0"
                              size="sm"
                              src={country.img}
                            />
                          }
                        >
                          {country.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  <div className="my-5 w-full">
                    <Textarea
                      isRequired
                      label="Your Message"
                      labelPlacement="outside"
                      size="lg"
                      onChange={handleChange}
                      fullWidth={true}
                      name="message"
                      isInvalid={checkBoolean(errors.message, touched.message)}
                      value={values.message}
                      errorMessage={errors.message}
                      placeholder="Enter your message"
                      className="w-full px-4"
                    />
                  </div>

                  <div className="flex flex-col items-center">
                    <ReCAPTCHA
                      ref={recaptchaRef as React.RefObject<ReCAPTCHA>}
                      sitekey="6LeR2PspAAAAADimK_U_PO5bHDo32rx05C99e8ON"
                    />
                    <Button
                      type="submit"
                      color="primary"
                      className="mt-8"
                      isLoading={loading ? true : false}
                    >
                      Send Message {<Icon icon="mdi:send" fontSize={20} />}
                    </Button>
                  </div>
                </form>
              )}
            </CardBody>
          </Card>
        </div>
        <div className="lg:w-full xl:w-full md:w-full sm:w-full ct-transfer min-h-80 ct-bg-white">
          <div className="lg:ct-image-container justify-center md:mx-auto px-8 flex items-center rounded-md">
            <img src={sectionImage} className="w-1/2" alt="" />
          </div>
        </div>
      </div>
    </div>
  );
}
